import { useMemo } from 'react';

import { Typography, useTheme } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers';
import { InvoiceStatus } from 'types/graphql';

import { Card, Spacer } from 'src/components';
import { Flex } from 'src/components/Flex';
import DisplayText from 'src/features/finance/sepaDirectDebits/components/BillingDetails/DisplayText';
import { formatDate } from 'src/utils/utils';

const DEFAULT_SPACER_PADDING = 1;
interface InvoiceDetailsProps {
    invoiceDate: Date;
    recipientType: 'MERCHANT' | 'PARTNER';
    recipientName: string;
    invoiceNumber: string;
    billingCycle: string;
    invoiceStatus: InvoiceStatus;
}

const InvoiceDetails = ({
    invoiceDate,
    invoiceNumber,
    billingCycle,
    invoiceStatus,
    recipientName,
    recipientType,
}: InvoiceDetailsProps) => {
    const theme = useTheme();
    const color = useMemo(() => {
        switch (invoiceStatus) {
            case 'PAID':
                return 'success';
            case 'PARTIALLY_PAID':
                return 'warning';
            case 'UNPAID':
                return 'error';
        }
    }, [invoiceStatus]);

    return (
        <Card color={theme.palette.text.primary} flex={1} gap="24px">
            <Typography fontSize="20px" fontWeight="500" lineHeight="30px">
                Invoice Details
            </Typography>
            <Flex alignItems="center" fontSize="16px" lineHeight="22px">
                <Typography fontWeight="600">Invoice To</Typography>
                <Typography fontWeight="400" marginLeft="24px">
                    {recipientType}
                </Typography>
            </Flex>
            <Flex flexDirection={'column'} gap={0.5}>
                <Typography fontWeight={600} fontSize="14px" lineHeight="21px">
                    Date
                </Typography>
                <Flex
                    color={theme.palette.common.white}
                    fontSize="16px"
                    fontWeight="700"
                    lineHeight="24px"
                    bgcolor={theme.palette.border.light}
                    gap="13px"
                    borderRadius="8px"
                    padding="8px"
                    width="max-content"
                    alignItems="center"
                >
                    <CalendarIcon />
                    <Typography>{formatDate({ date: invoiceDate })}</Typography>
                </Flex>
            </Flex>
            <Flex alignItems="flex-start">
                <DisplayText
                    label={recipientType === 'MERCHANT' ? 'Merchant' : 'Partner'}
                    value={recipientName}
                />
                <Spacer padding={DEFAULT_SPACER_PADDING} />
                <DisplayText label="Invoice Number" value={invoiceNumber} />
            </Flex>
            <Flex alignItems="flex-start">
                <DisplayText label="Billing Cycle" value={billingCycle} />
                <Spacer padding={DEFAULT_SPACER_PADDING} />
                <DisplayText
                    label="Invoice Status"
                    value={invoiceStatus?.replace(/_/g, ' ')}
                    color={color}
                />
            </Flex>
        </Card>
    );
};
export default InvoiceDetails;
